import React from 'react';
import { cn } from '@/utils/helpers';

// 警告框变体类型
export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

// 警告框属性接口
export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  closable?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
}

// 变体样式映射 - Fluent Design 风格
const variantStyles: Record<AlertVariant, string> = {
  info: 'acrylic border-blue-200 text-blue-800',
  success: 'acrylic border-green-200 text-green-800', 
  warning: 'acrylic border-yellow-200 text-yellow-800',
  error: 'acrylic border-red-200 text-red-800'
};

// 图标颜色映射
const iconColorStyles: Record<AlertVariant, string> = {
  info: 'text-blue-500',
  success: 'text-green-500',
  warning: 'text-yellow-500',
  error: 'text-red-500'
};

// 默认图标组件
const DefaultIcons: Record<AlertVariant, React.ReactNode> = {
  info: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
  success: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  )
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  description,
  icon,
  closable = false,
  onClose,
  children,
  className,
  ...props
}) => {
  const displayIcon = icon !== null ? (icon || DefaultIcons[variant]) : null;

  return (
    <div
      className={cn(
        // 基础样式
        'rounded-lg border p-4 transition-all duration-200',
        // 变体样式
        variantStyles[variant],
        // 自定义类名
        className
      )}
      role="alert"
      {...props}
    >
      <div className="flex items-start">
        {/* 图标 */}
        {displayIcon && (
          <div className={cn('flex-shrink-0 mr-3', iconColorStyles[variant])}>
            {displayIcon}
          </div>
        )}

        {/* 内容 */}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-sm font-medium mb-1">
              {title}
            </h3>
          )}
          
          {description && (
            <div className="text-sm opacity-90">
              {description}
            </div>
          )}
          
          {children && (
            <div className="mt-2">
              {children}
            </div>
          )}
        </div>

        {/* 关闭按钮 */}
        {closable && onClose && (
          <div className="flex-shrink-0 ml-3">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                'hover:bg-black hover:bg-opacity-10 transition-colors duration-200',
                iconColorStyles[variant],
                'focus:ring-current'
              )}
              aria-label="关闭"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// 简单警告框组件
export interface SimpleAlertProps extends Omit<AlertProps, 'title' | 'description' | 'children'> {
  message: string;
}

export const SimpleAlert: React.FC<SimpleAlertProps> = ({
  message,
  ...props
}) => {
  return (
    <Alert {...props} description={message} />
  );
};

// 操作警告框组件
export interface ActionAlertProps extends AlertProps {
  actions?: React.ReactNode;
}

export const ActionAlert: React.FC<ActionAlertProps> = ({
  actions,
  children,
  ...props
}) => {
  return (
    <Alert {...props}>
      {children}
      {actions && (
        <div className="mt-3 flex space-x-2">
          {actions}
        </div>
      )}
    </Alert>
  );
};

// 列表警告框组件
export interface ListAlertProps extends Omit<AlertProps, 'children'> {
  items: string[];
}

export const ListAlert: React.FC<ListAlertProps> = ({
  items,
  ...props
}) => {
  return (
    <Alert {...props}>
      <ul className="list-disc list-inside space-y-1 text-sm">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </Alert>
  );
};

// Toast 样式警告框组件
export interface ToastAlertProps extends AlertProps {
  duration?: number;
  position?: 'top' | 'bottom';
}

export const ToastAlert: React.FC<ToastAlertProps> = ({
  duration = 3000,
  position = 'top',
  onClose,
  ...props
}) => {
  React.useEffect(() => {
    if (duration > 0 && onClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div
      className={cn(
        'fixed left-1/2 transform -translate-x-1/2 z-50 max-w-sm w-full mx-4',
        position === 'top' ? 'top-4' : 'bottom-4'
      )}
    >
      <Alert
        {...props}
        closable={true}
        onClose={onClose}
        className={cn(
          'shadow-lg border-0',
          props.className
        )}
      />
    </div>
  );
};

export default Alert;